import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, MapPin, CircleDot, ArrowRight, CheckCircle2, ChevronDown, ChevronUp } from "lucide-react";
import EngagementBadge from "@/components/EngagementBadge";
import SessionMetaRow from "@/components/SessionMetaRow";

// Phase 25.13p — Click-through drawer that shows every visitor session
// that landed from a specific organic referrer (e.g. `linkedin.com`).
// Surfaces journey paths and conversion flags so super-admin can see
// "did anyone who clicked my LinkedIn post actually convert?"
//
// Props:
//   referrer (string): cleaned referrer hostname OR "(direct)"
//   days (number): lookback window
//   includeInternal (bool): mirror the AdminVisitors INTERNAL toggle so
//     drilling into `app.emergent.sh` actually returns its sessions.
//   onClose ()
export default function ReferrerSessionsDrawer({ referrer, days = 30, includeInternal = false, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // session_id

  useEffect(() => {
    if (!referrer) return;
    let cancel = false;
    setLoading(true);
    const qs = new URLSearchParams({
      referrer,
      days: String(days),
      include_internal: includeInternal ? "true" : "false",
    }).toString();
    api.get(`/admin/visitors/referrer-sessions?${qs}`)
      .then(r => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setData({ sessions: [], session_count: 0, converted_count: 0 }); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [referrer, days, includeInternal]);

  useEffect(() => {
    if (!referrer) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [referrer, onClose]);

  if (!referrer) return null;

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-sm flex items-stretch justify-end"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Visitor sessions from ${referrer}`}
      data-testid="referrer-sessions-drawer"
    >
      <div
        className="relative bg-[#040404] border-l border-[#FFB000]/30 w-full max-w-2xl h-full overflow-y-auto shadow-[0_0_60px_-10px_rgba(255,176,0,0.4)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* terminal header */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-3 border-b border-zinc-900 bg-[#050505]">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-2 h-2 bg-[#FF3B30]" />
            <div className="w-2 h-2 bg-[#FFB000]" />
            <div className="w-2 h-2 bg-[#00E676]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 ml-2 truncate">
              DARA://sessions · <span className="text-[#FFB000]">{referrer}</span>
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="referrer-drawer-close"
            className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FFB000] flex items-center gap-1 transition-colors"
            aria-label="Close drawer"
          >
            CLOSE <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 sm:p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FFB000] mb-2">
            // VISITORS FROM THIS REFERRER · LAST {days}D
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight mb-3 break-words">
            {referrer}
          </h2>

          {loading && (
            <div className="font-mono text-[11px] text-zinc-600 py-8 text-center">Loading…</div>
          )}

          {!loading && data && (
            <>
              <div className="grid grid-cols-2 gap-px bg-zinc-900 border border-zinc-900 mb-6">
                <div className="bg-black p-4">
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Unique sessions</div>
                  <div className="font-mono text-2xl text-white mt-1">{data.session_count}</div>
                </div>
                <div className="bg-black p-4">
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Converted</div>
                  <div className={`font-mono text-2xl mt-1 ${data.converted_count > 0 ? "text-[#00E676]" : "text-zinc-600"}`}>
                    {data.converted_count}
                  </div>
                </div>
              </div>

              {data.sessions.length === 0 ? (
                <div className="font-mono text-[11px] text-zinc-600 leading-relaxed py-8 text-center border border-dashed border-zinc-900">
                  No sessions matched. Either the referrer name has drifted or the cohort already aged out of the {days}-day window.
                </div>
              ) : (
                <ul className="space-y-2">
                  {data.sessions.map((s, idx) => {
                    const isOpen = expanded === s.session_id;
                    const firstDay = (s.first_seen || "").slice(0, 10);
                    const lastDay = (s.last_seen || "").slice(0, 10);
                    return (
                      <li key={s.session_id} className="border border-zinc-900 bg-[#070707]" data-testid={`referrer-session-${idx}`}>
                        <button
                          type="button"
                          onClick={() => setExpanded(isOpen ? null : s.session_id)}
                          className="w-full text-left px-4 py-3 hover:bg-zinc-950/50"
                          aria-expanded={isOpen}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="font-mono text-[9px] text-zinc-700 w-6">{String(idx + 1).padStart(2, "0")}</span>
                              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                <span className="font-mono text-[11px] text-zinc-300 truncate">{s.landing_path}</span>
                                {s.engagement && <EngagementBadge engagement={s.engagement} compact />}
                                {s.country && (
                                  <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 flex-shrink-0">
                                    <MapPin className="w-2.5 h-2.5" /> {s.country}
                                  </span>
                                )}
                                {s.converted && (
                                  <span className="font-mono text-[9px] uppercase tracking-widest text-[#00E676] flex items-center gap-1 flex-shrink-0 border border-[#00E676]/40 bg-[#00E676]/5 px-1.5 py-px">
                                    <CheckCircle2 className="w-2.5 h-2.5" /> CONVERTED
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="font-mono text-[10px] text-[#FFB000]">{s.pageviews} pv</span>
                              {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
                            </div>
                          </div>
                          <div className="pl-8">
                            <SessionMetaRow session={s} />
                          </div>
                        </button>
                        {isOpen && (
                          <div className="border-t border-zinc-900 px-4 py-3 bg-black">
                            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600 mb-2">
                              Journey · {firstDay}{lastDay !== firstDay ? ` → ${lastDay}` : ""}
                            </div>
                            <ol className="space-y-1.5">
                              {s.journey.map((step, j) => (
                                <li key={j} className="flex items-center gap-2 font-mono text-[11px] text-zinc-400">
                                  <CircleDot className="w-2.5 h-2.5 text-[#FFB000] flex-shrink-0" />
                                  <span className="truncate">{step.path}</span>
                                  <span className="text-zinc-700 ml-auto">{(step.created_at || "").slice(11, 19)}Z</span>
                                </li>
                              ))}
                            </ol>
                            <div className="mt-3 pt-3 border-t border-zinc-900 font-mono text-[9px] uppercase tracking-widest text-zinc-700 break-all">
                              session_id: {s.session_id}
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              {data.sessions.length > 0 && data.session_count > data.sessions.length && (
                <div className="mt-4 font-mono text-[10px] uppercase tracking-widest text-zinc-600 text-center">
                  Showing {data.sessions.length} of {data.session_count} — drill into AdminVisitors for the full list.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
